import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { QuotaIndicator } from "@/components/QuotaIndicator";
import { toast } from "sonner";
import { FolderKanban, Globe, Sparkles, CheckCircle2, Plus, ArrowRight, FolderPlus } from "lucide-react";

const StatCard = ({ icon: Icon, label, value }) => (
  <Card className="p-4">
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="font-heading text-2xl font-semibold leading-none">{value}</div>
        <div className="mt-1 text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { t, user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createPortfolio = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data: p } = await api.post("/portfolios", { name, description: desc });
      toast.success(t("portfolioCreated"));
      setOpen(false); setName(""); setDesc("");
      navigate(`/app/portfolios/${p.id}`);
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const s = data?.stats || {};
  const recent = data?.recent_pitches || [];

  const createDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="dashboard-new-portfolio-button">
          <Plus className="h-4 w-4" /> {t("newPortfolio")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("createPortfolio")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("portfolioName")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="portfolio-name-input" placeholder="Tech Domains" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("portfolioDesc")} <span className="text-xs text-muted-foreground">({t("optional")})</span></Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="portfolio-desc-input" rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
          <Button onClick={createPortfolio} disabled={creating || !name.trim()} data-testid="portfolio-create-submit">
            {creating ? t("loading") : t("create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{t("dashTitle")}</h1>
          <p className="text-sm text-muted-foreground">{t("welcomeBack")}, {user?.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><QuotaIndicator /></div>
          {createDialog}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard icon={FolderKanban} label={t("statPortfolios")} value={s.portfolios || 0} />
        <StatCard icon={Globe} label={t("statDomains")} value={s.domains_total || 0} />
        <StatCard icon={Sparkles} label={t("statPitches")} value={s.pitches_total || 0} />
        <StatCard icon={CheckCircle2} label={t("statSold")} value={s.domains_sold || 0} />
      </div>

      <Card className="p-5">
        <h2 className="font-heading text-lg font-semibold">{t("recentPitches")}</h2>
        {recent.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">{t("noRecentPitches")}</p>
        ) : (
          <div className="mt-4 divide-y">
            {recent.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/app/domains/${p.domain_id}`)}
                className="flex w-full items-center justify-between py-3 text-left transition-colors hover:bg-secondary/40"
                data-testid="recent-pitch-item"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary"><Sparkles className="h-4 w-4" /></span>
                  <div>
                    <div className="font-mono text-sm font-medium">{p.domain_name}</div>
                    <div className="text-xs text-muted-foreground">{p.valuation_range?.slice(0, 60)}</div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </Card>

      {(!s.portfolios || s.portfolios === 0) && (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary"><FolderPlus className="h-6 w-6" /></span>
          <h3 className="font-heading text-lg font-semibold">{t("noPortfolios")}</h3>
          <p className="max-w-sm text-sm text-muted-foreground">{t("noPortfoliosDesc")}</p>
          {createDialog}
        </Card>
      )}
    </div>
  );
}
